import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    format,
    parseISO,
    isWithinInterval,
    startOfDay,
    endOfDay,
    subDays,
    startOfMonth,
} from 'date-fns';
import { RefreshCw, CalendarIcon, ChevronDown, Check, MapPin, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { shifts as shiftsApi } from '@/lib/api';
import { useAuthContext } from '@/contexts/AuthContext';
import { useRBAC } from '@/hooks/useRBAC';
import { RBACGuard } from '@/components/guards/RBACGuard';
import type { ShiftLog, DelayEntry } from '@/types/drilling';
import DrillingMetricsCards from '@/components/dashboard/DrillingMetricsCards';
import DrillingCharts from '@/components/dashboard/DrillingCharts';
import CpmAnalysis from '@/components/dashboard/CpmAnalysis';
import ConsumableTables from '@/components/dashboard/ConsumableTables';
import PatternProgress from '@/components/dashboard/PatternProgress';
import OperatorPerformance from '@/components/dashboard/OperatorPerformance';
import { PresentationButton } from '@/components/dashboard/PresentationMode';
import type { DrillPattern } from '@/types/pattern';
import { generateSampleDrillingData } from '@/lib/sampleDrillingData';

const DATE_PRESETS = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: 'MTD', days: null as number | null },
];

export default function UniversalDashboard() {
    const { user, profile } = useAuthContext();
    const { hasApp, can } = useRBAC();

    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date | undefined>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [endDate, setEndDate] = useState<Date | undefined>(new Date());

    const DEV_PREVIEW = import.meta.env.DEV && !import.meta.env.VITE_API_BASE_URL;

    // Approved shift logs for this org. Gated on auth (user/profile), which is
    // always present here since UniversalDashboard renders behind ProtectedRoute.
    // In local dev, falls back to generated sample data when the DB has no shifts.
    const {
        data: rawShiftLogs = [],
        isFetching: loading,
        refetch,
    } = useQuery({
        queryKey: ['shift-logs', profile?.org_id, user?.id],
        enabled: Boolean(user && profile),
        queryFn: async () => {
            if (DEV_PREVIEW) {
                const sample = generateSampleDrillingData();
                return sample.map((s) => ({ app_slug: 'operator' as const, shift_data: s as unknown }));
            }
            const rows = await shiftsApi.list({ limit: 500 });
            const approved = rows
                .filter((r) => r.status === 'approved')
                .map((r) => ({ app_slug: r.app_slug, shift_data: r.shift_data as unknown }));
            if (import.meta.env.DEV && approved.length === 0) {
                const sample = generateSampleDrillingData();
                return sample.map((s) => ({ app_slug: 'operator' as const, shift_data: s as unknown }));
            }
            return approved;
        },
    });

    const drillingLogs = useMemo(() => {
        return rawShiftLogs
            .filter((r) => r.app_slug === 'operator')
            .map((r) => r.shift_data as unknown as ShiftLog);
    }, [rawShiftLogs]);

    const drillPatterns = useMemo(() => {
        return rawShiftLogs
            .filter((r) => r.app_slug === 'operator-pattern')
            .map((r) => r.shift_data as unknown as DrillPattern);
    }, [rawShiftLogs]);

    const availableSites = useMemo(() => {
        const sites = new Set<string>();
        drillingLogs.forEach((l) => {
            if (l.shiftDetails?.site) sites.add(l.shiftDetails.site);
        });
        return [...sites].sort();
    }, [drillingLogs]);

    const availableAreas = useMemo(() => {
        const areas = new Set<string>();
        drillingLogs.forEach((l) => {
            if (l.shiftDetails?.location) areas.add(l.shiftDetails.location);
            (l.drillHoles ?? []).forEach((h) => {
                if (h.workArea) areas.add(h.workArea);
            });
        });
        return [...areas].sort();
    }, [drillingLogs]);

    const filteredDrillingLogs = useMemo(() => {
        let logs = drillingLogs;
        if (startDate && endDate) {
            logs = logs.filter((log) => {
                if (!log.shiftDetails?.date) return false;
                const d = parseISO(log.shiftDetails.date);
                if (Number.isNaN(d.getTime())) return false;
                return isWithinInterval(d, {
                    start: startOfDay(startDate),
                    end: endOfDay(endDate),
                });
            });
        }
        if (selectedSites.length > 0) {
            logs = logs.filter((log) => selectedSites.includes(log.shiftDetails?.site ?? ''));
        }
        if (selectedAreas.length > 0) {
            logs = logs.filter((log) => selectedAreas.includes(log.shiftDetails?.location ?? ''));
        }
        return logs;
    }, [drillingLogs, startDate, endDate, selectedSites, selectedAreas]);

    // Previous period (same duration, immediately before current window)
    const prevDrillingLogs = useMemo(() => {
        if (!startDate || !endDate) return [];
        const duration = endDate.getTime() - startDate.getTime();
        const prevEnd = new Date(startDate.getTime() - 86_400_000);
        const prevStart = new Date(prevEnd.getTime() - duration);
        return drillingLogs.filter((log) => {
            if (!log.shiftDetails?.date) return false;
            const d = parseISO(log.shiftDetails.date);
            if (Number.isNaN(d.getTime())) return false;
            return isWithinInterval(d, { start: startOfDay(prevStart), end: endOfDay(prevEnd) });
        });
    }, [drillingLogs, startDate, endDate]);

    const drillingMetrics = useMemo(() => {
        const logs = filteredDrillingLogs;
        const shiftCount = logs.length;
        const totalHoles = logs.reduce((s, l) => s + (l.drillHoles?.length ?? 0), 0);
        const totalMetres = logs.reduce(
            (s, l) => s + (l.drillHoles?.reduce((a, h) => a + (h.depth ?? 0), 0) ?? 0),
            0
        );
        const totalDrillingSeconds = logs.reduce(
            (s, l) =>
                s + (l.drillHoles?.reduce((a, h) => a + (h.drillingTimeSeconds ?? 0), 0) ?? 0),
            0
        );
        const rigMetres = (rigType: string) =>
            logs
                .filter((l) => l.shiftDetails?.rigType === rigType)
                .reduce(
                    (s, l) => s + (l.drillHoles?.reduce((a, h) => a + (h.depth ?? 0), 0) ?? 0),
                    0
                );
        const d65Metres = rigMetres('D65');
        const md6250Metres = rigMetres('MD6250');

        const avgBitByDiameter = (diameter: number) => {
            const vals = logs
                .flatMap((l) => l.consumables ?? [])
                .filter(
                    (c) =>
                        c.type === 'drill-bit' &&
                        c.bitDiameter === diameter &&
                        (c.metresDrilled ?? 0) > 0
                )
                .map((c) => c.metresDrilled ?? 0);
            return vals.length > 0 ? vals.reduce((s, m) => s + m, 0) / vals.length : 0;
        };
        const avgMetresPerBit165 = avgBitByDiameter(165);
        const avgMetresPerBit203 = avgBitByDiameter(203);
        const allBitMetres = logs
            .flatMap((l) => l.consumables ?? [])
            .filter((c) => c.type === 'drill-bit' && (c.metresDrilled ?? 0) > 0)
            .map((c) => c.metresDrilled ?? 0);
        const avgMetresPerBit =
            allBitMetres.length > 0
                ? allBitMetres.reduce((s, m) => s + m, 0) / allBitMetres.length
                : 0;

        const redrillHoles = logs.reduce(
            (s, l) => s + (l.drillHoles?.filter((h) => h.redrill === true).length ?? 0),
            0
        );
        const totalDelaySeconds = logs
            .flatMap((l) => l.delays ?? [])
            .reduce((s, d) => s + (d.timeSeconds ?? 0), 0);
        const penetrationRate =
            totalDrillingSeconds > 0 ? (totalMetres / totalDrillingSeconds) * 3600 : 0;
        const drillAvailability =
            shiftCount > 0
                ? ((shiftCount * 43_200 - totalDelaySeconds) / (shiftCount * 43_200)) * 100
                : 0;

        return {
            totalHoles,
            totalMetres,
            d65Metres,
            md6250Metres,
            totalDrillingSeconds,
            avgDepth: totalHoles > 0 ? totalMetres / totalHoles : 0,
            avgTimePerHole: totalHoles > 0 ? totalDrillingSeconds / totalHoles : 0,
            avgMetresPerBit,
            avgMetresPerBit165,
            avgMetresPerBit203,
            penetrationRate,
            redrillHoles,
            redrillPct: totalHoles > 0 ? (redrillHoles / totalHoles) * 100 : 0,
            drillAvailability,
        };
    }, [filteredDrillingLogs]);

    const prevMetrics = useMemo(() => {
        const logs = prevDrillingLogs;
        if (logs.length === 0) return undefined;
        const totalMetres = logs.reduce(
            (s, l) => s + (l.drillHoles?.reduce((a, h) => a + (h.depth ?? 0), 0) ?? 0),
            0
        );
        const totalDrillingSeconds = logs.reduce(
            (s, l) =>
                s + (l.drillHoles?.reduce((a, h) => a + (h.drillingTimeSeconds ?? 0), 0) ?? 0),
            0
        );
        const totalDelaySeconds = logs
            .flatMap((l) => l.delays ?? [])
            .reduce((s, d) => s + (d.timeSeconds ?? 0), 0);
        const totalHoles = logs.reduce((s, l) => s + (l.drillHoles?.length ?? 0), 0);
        const redrillHoles = logs.reduce(
            (s, l) => s + (l.drillHoles?.filter((h) => h.redrill === true).length ?? 0),
            0
        );
        return {
            totalMetres,
            avgMetresPerShift: logs.length > 0 ? totalMetres / logs.length : 0,
            penetrationRate:
                totalDrillingSeconds > 0 ? (totalMetres / totalDrillingSeconds) * 3600 : 0,
            drillAvailability:
                logs.length > 0
                    ? ((logs.length * 43_200 - totalDelaySeconds) / (logs.length * 43_200)) * 100
                    : 0,
            totalDelaySeconds,
            redrillPct: totalHoles > 0 ? (redrillHoles / totalHoles) * 100 : 0,
        };
    }, [prevDrillingLogs]);

    const drillingDelayMetrics = useMemo(() => {
        const allDelays: DelayEntry[] = filteredDrillingLogs.flatMap((l) => l.delays ?? []);
        const totalDelaySeconds = allDelays.reduce((s, d) => s + (d.timeSeconds ?? 0), 0);
        const byType: Record<string, number> = {};
        allDelays.forEach((d) => {
            const t = d.delayType?.replace(/-/g, ' ') ?? 'Unknown';
            byType[t] = (byType[t] ?? 0) + (d.timeSeconds ?? 0);
        });
        const delayBreakdown = Object.entries(byType)
            .map(([name, value]) => ({ name, value: Math.round(value / 60) }))
            .sort((a, b) => b.value - a.value);
        return { totalDelaySeconds, totalDelayCount: allDelays.length, delayBreakdown };
    }, [filteredDrillingLogs]);

    const dailyDrillingTrend = useMemo(() => {
        const byDate: Record<string, number> = {};
        filteredDrillingLogs.forEach((log) => {
            const date = log.shiftDetails?.date;
            if (!date) return;
            byDate[date] =
                (byDate[date] ?? 0) +
                (log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0);
        });
        return Object.entries(byDate)
            .map(([date, metres]) => ({
                date: format(parseISO(date), 'MMM dd'),
                metres: Math.round(metres * 10) / 10,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [filteredDrillingLogs]);

    const penetrationTrendData = useMemo(() => {
        const byDate: Record<string, { metres: number; seconds: number }> = {};
        filteredDrillingLogs.forEach((log) => {
            const date = log.shiftDetails?.date;
            if (!date) return;
            if (!byDate[date]) byDate[date] = { metres: 0, seconds: 0 };
            byDate[date].metres += log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            byDate[date].seconds +=
                log.drillHoles?.reduce((s, h) => s + (h.drillingTimeSeconds ?? 0), 0) ?? 0;
        });
        return Object.entries(byDate)
            .filter(([, d]) => d.seconds > 0)
            .map(([date, d]) => ({
                date: format(parseISO(date), 'MMM dd'),
                rate: Math.round((d.metres / d.seconds) * 3600 * 10) / 10,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [filteredDrillingLogs]);

    const dayNightData = useMemo(() => {
        const buckets: Record<string, { metres: number; shifts: number }> = {
            Day: { metres: 0, shifts: 0 },
            Night: { metres: 0, shifts: 0 },
        };
        filteredDrillingLogs.forEach((log) => {
            const key = (log.shiftDetails?.shiftType ?? 'day') === 'night' ? 'Night' : 'Day';
            const metres = log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            buckets[key].metres += metres;
            buckets[key].shifts++;
        });
        return Object.entries(buckets).map(([shift, d]) => ({
            shift,
            metresPerShift: d.shifts > 0 ? Math.round(d.metres / d.shifts) : 0,
            totalMetres: Math.round(d.metres),
        }));
    }, [filteredDrillingLogs]);

    const rigUtilisationData = useMemo(() => {
        const rigMap: Record<
            string,
            { metres: number; shifts: number; drillingSeconds: number; delaySeconds: number }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            const rig = log.shiftDetails?.rigId;
            if (!rig) return;
            if (!rigMap[rig])
                rigMap[rig] = { metres: 0, shifts: 0, drillingSeconds: 0, delaySeconds: 0 };
            rigMap[rig].metres += log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            rigMap[rig].shifts++;
            rigMap[rig].drillingSeconds +=
                log.drillHoles?.reduce((s, h) => s + (h.drillingTimeSeconds ?? 0), 0) ?? 0;
            rigMap[rig].delaySeconds += (log.delays ?? []).reduce(
                (s, d) => s + (d.timeSeconds ?? 0),
                0
            );
        });
        return Object.entries(rigMap)
            .map(([rig, d]) => ({
                rig,
                metres: Math.round(d.metres * 10) / 10,
                shifts: d.shifts,
                avgDelayMins: d.shifts > 0 ? Math.round(d.delaySeconds / d.shifts / 60) : 0,
                availability:
                    d.shifts > 0
                        ? Math.round(
                              ((d.shifts * 43_200 - d.delaySeconds) / (d.shifts * 43_200)) * 100
                          )
                        : 0,
            }))
            .sort((a, b) => b.metres - a.metres);
    }, [filteredDrillingLogs]);

    const operatorPerformanceData = useMemo(() => {
        const opMap: Record<
            string,
            { totalMetres: number; shiftCount: number; bitMetresTotal: number; bitCount: number }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            const name = log.shiftDetails?.operatorName;
            if (!name) return;
            if (!opMap[name])
                opMap[name] = { totalMetres: 0, shiftCount: 0, bitMetresTotal: 0, bitCount: 0 };
            const metres = log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            opMap[name].totalMetres += metres;
            opMap[name].shiftCount++;
            (log.consumables ?? []).forEach((c) => {
                if (c.type === 'drill-bit' && (c.metresDrilled ?? 0) > 0) {
                    opMap[name].bitMetresTotal += c.metresDrilled ?? 0;
                    opMap[name].bitCount++;
                }
            });
        });
        return Object.entries(opMap)
            .map(([fullName, d]) => ({
                name: fullName.split(' ')[0],
                fullName,
                metresPerShift: d.shiftCount > 0 ? Math.round(d.totalMetres / d.shiftCount) : 0,
                metresPerBit: d.bitCount > 0 ? Math.round(d.bitMetresTotal / d.bitCount) : 0,
            }))
            .filter((d) => d.metresPerShift > 0)
            .sort((a, b) => b.metresPerShift - a.metresPerShift);
    }, [filteredDrillingLogs]);

    // Operator leaderboards — best bit life, best hammer life, fastest pen rate, lowest redrill %
    const operatorLeaderboards = useMemo(() => {
        // Best bit life per operator (max metres on a single bit)
        const bestBit: Record<string, { operator: string; serial: string; metres: number }> = {};
        // Best hammer life per operator
        const bestHammer: Record<string, { operator: string; serial: string; metres: number }> = {};
        // Pen rate samples per operator
        const penRates: Record<string, number[]> = {};
        // Redrill tracking per operator
        const redrills: Record<string, { total: number; redrilled: number }> = {};

        for (const log of filteredDrillingLogs) {
            const op = log.shiftDetails?.operatorName;
            if (!op) continue;

            // Redrills
            if (!redrills[op]) redrills[op] = { total: 0, redrilled: 0 };
            for (const h of log.drillHoles ?? []) {
                redrills[op].total++;
                if (h.redrill) redrills[op].redrilled++;
            }

            // Pen rate per hole
            for (const h of log.drillHoles ?? []) {
                if ((h.drillingTimeSeconds ?? 0) > 0 && (h.depth ?? 0) > 0) {
                    if (!penRates[op]) penRates[op] = [];
                    penRates[op].push((h.depth / h.drillingTimeSeconds!) * 3600);
                }
            }

            // Best consumable life
            for (const c of log.consumables ?? []) {
                const metres = c.metresDrilled ?? 0;
                if (metres <= 0 || !c.serialNumber) continue;
                if (c.type === 'drill-bit') {
                    if (!bestBit[op] || metres > bestBit[op].metres) {
                        bestBit[op] = { operator: op, serial: c.serialNumber, metres };
                    }
                } else if (c.type === 'hammer') {
                    if (!bestHammer[op] || metres > bestHammer[op].metres) {
                        bestHammer[op] = { operator: op, serial: c.serialNumber, metres };
                    }
                }
            }
        }

        return {
            bestBitLife: Object.values(bestBit)
                .sort((a, b) => b.metres - a.metres)
                .slice(0, 5),
            bestHammerLife: Object.values(bestHammer)
                .sort((a, b) => b.metres - a.metres)
                .slice(0, 5),
            fastestPenRate: Object.entries(penRates)
                .map(([op, rates]) => ({
                    operator: op,
                    avgPenRate: Math.round((rates.reduce((s, r) => s + r, 0) / rates.length) * 10) / 10,
                    holes: rates.length,
                }))
                .sort((a, b) => b.avgPenRate - a.avgPenRate)
                .slice(0, 5),
            lowestRedrill: Object.entries(redrills)
                .filter(([, d]) => d.total >= 10)
                .map(([op, d]) => ({
                    operator: op,
                    rate: Math.round((d.redrilled / d.total) * 1000) / 10,
                    total: d.total,
                    redrilled: d.redrilled,
                }))
                .sort((a, b) => a.rate - b.rate)
                .slice(0, 5),
        };
    }, [filteredDrillingLogs]);

    const metresByRigData = useMemo(() => {
        const rigMap: Record<string, number> = {};
        filteredDrillingLogs.forEach((log) => {
            const rig = log.shiftDetails?.rigId;
            if (!rig) return;
            const metres = log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            rigMap[rig] = (rigMap[rig] ?? 0) + metres;
        });
        return Object.entries(rigMap)
            .map(([rig, metres]) => ({ rig, metres: Math.round(metres * 10) / 10 }))
            .sort((a, b) => b.metres - a.metres);
    }, [filteredDrillingLogs]);

    const metresPerBitData = useMemo(() => {
        const bitMap: Record<
            string,
            {
                serial: string;
                manufacturer: string;
                metres: number;
                diameter: number;
                bitType: string;
            }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            (log.consumables ?? []).forEach((c) => {
                if (c.type === 'drill-bit' && c.serialNumber) {
                    if (
                        !bitMap[c.serialNumber] ||
                        c.metresDrilled > bitMap[c.serialNumber].metres
                    ) {
                        bitMap[c.serialNumber] = {
                            serial: c.serialNumber,
                            manufacturer: c.manufacturer ?? 'Unknown',
                            metres: c.metresDrilled ?? 0,
                            diameter: c.bitDiameter ?? 0,
                            bitType: c.bitType ?? '',
                        };
                    }
                }
            });
        });
        const all = Object.values(bitMap).filter((b) => b.diameter > 0);
        const diameters = [...new Set(all.map((b) => b.diameter))].sort((a, b) => a - b);
        return diameters.map((dia) => {
            const group = all.filter((b) => b.diameter === dia).sort((a, b) => b.metres - a.metres);
            const toEntry = (b: (typeof group)[0]) => ({
                name: b.serial,
                metres: Math.round(b.metres * 10) / 10,
                manufacturer: b.manufacturer,
                label: `${b.serial} (${b.manufacturer})`,
            });
            return {
                diameter: dia,
                label: `${dia}mm ${group[0]?.bitType ?? ''}`.trim(),
                top: group.slice(0, 5).map(toEntry),
                bottom: group.length > 5 ? group.slice(-5).reverse().map(toEntry) : [],
            };
        });
    }, [filteredDrillingLogs]);

    const metresPerHammerData = useMemo(() => {
        const hammerMap: Record<
            string,
            { serial: string; manufacturer: string; metres: number; name: string; diameter: number }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            (log.consumables ?? []).forEach((c) => {
                if (c.type === 'hammer' && c.serialNumber) {
                    if (
                        !hammerMap[c.serialNumber] ||
                        c.metresDrilled > hammerMap[c.serialNumber].metres
                    ) {
                        hammerMap[c.serialNumber] = {
                            serial: c.serialNumber,
                            manufacturer: c.manufacturer ?? 'Unknown',
                            metres: c.metresDrilled ?? 0,
                            name: c.name ?? 'Hammer',
                            diameter: c.bitDiameter ?? 0,
                        };
                    }
                }
            });
        });
        const all = Object.values(hammerMap);
        const sizes = [...new Set(all.map((h) => h.name))].sort();
        return sizes.map((size) => {
            const group = all.filter((h) => h.name === size).sort((a, b) => b.metres - a.metres);
            const toEntry = (h: (typeof group)[0]) => ({
                name: h.serial,
                metres: Math.round(h.metres * 10) / 10,
                manufacturer: h.manufacturer,
                label: `${h.serial} (${h.manufacturer})`,
            });
            return {
                size,
                top: group.slice(0, 3).map(toEntry),
                bottom: group.length > 3 ? group.slice(-3).reverse().map(toEntry) : [],
            };
        });
    }, [filteredDrillingLogs]);

    const metresPerRodData = useMemo(() => {
        const rodMap: Record<
            string,
            { serial: string; manufacturer: string; metres: number; name: string; diameter: number }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            (log.consumables ?? []).forEach((c) => {
                if (c.type === 'drill-rod' && c.serialNumber) {
                    if (
                        !rodMap[c.serialNumber] ||
                        c.metresDrilled > rodMap[c.serialNumber].metres
                    ) {
                        rodMap[c.serialNumber] = {
                            serial: c.serialNumber,
                            manufacturer: c.manufacturer ?? 'Unknown',
                            metres: c.metresDrilled ?? 0,
                            name: c.name ?? 'Drill Rod',
                            diameter: c.bitDiameter ?? 0,
                        };
                    }
                }
            });
        });
        const all = Object.values(rodMap).filter((r) => r.diameter > 0);
        const sizes = [...new Set(all.map((r) => r.name))].sort();
        return sizes.map((size) => {
            const group = all.filter((r) => r.name === size).sort((a, b) => b.metres - a.metres);
            const toEntry = (r: (typeof group)[0]) => ({
                name: r.serial,
                metres: Math.round(r.metres * 10) / 10,
                manufacturer: r.manufacturer,
                label: `${r.serial} (${r.manufacturer})`,
            });
            return {
                size,
                top: group.slice(0, 5).map(toEntry),
                bottom: group.length > 5 ? group.slice(-5).reverse().map(toEntry) : [],
            };
        });
    }, [filteredDrillingLogs]);

    // Hammer wear curves — cumulative metres vs pen rate per serial across shifts
    const hammerWearData = useMemo(() => {
        // Collect per-shift data points for each hammer serial
        const bySerial: Record<
            string,
            {
                serial: string;
                size: string;
                points: { date: string; shiftMetres: number; penRate: number }[];
            }
        > = {};
        filteredDrillingLogs.forEach((log) => {
            (log.consumables ?? []).forEach((c) => {
                if (c.type !== 'hammer' || !c.serialNumber) return;
                const linked = c.linkedHoleIds ?? [];
                const holes =
                    linked.length > 0
                        ? (log.drillHoles ?? []).filter((h) => linked.includes(h.id))
                        : (log.drillHoles ?? []);
                const totalM = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
                const totalS = holes.reduce((s, h) => s + (h.drillingTimeSeconds ?? 0), 0);
                if (totalS <= 0 || totalM <= 0) return;
                if (!bySerial[c.serialNumber]) {
                    bySerial[c.serialNumber] = {
                        serial: c.serialNumber,
                        size: c.name ?? 'Hammer',
                        points: [],
                    };
                }
                bySerial[c.serialNumber].points.push({
                    date: log.shiftDetails?.date ?? '',
                    shiftMetres: totalM,
                    penRate: (totalM / totalS) * 3600,
                });
            });
        });
        // Build cumulative curves with rolling average to smooth shift-to-shift noise
        const WINDOW = 5;
        return Object.values(bySerial)
            .filter((h) => h.points.length >= 3)
            .map((h) => {
                h.points.sort((a, b) => a.date.localeCompare(b.date));
                let cumMetres = 0;
                const raw = h.points.map((p) => {
                    cumMetres += p.shiftMetres;
                    return { cumulativeMetres: Math.round(cumMetres), penRate: p.penRate };
                });
                // Rolling average
                const curve = raw.map((pt, i) => {
                    const start = Math.max(0, i - WINDOW + 1);
                    const window = raw.slice(start, i + 1);
                    const avg = window.reduce((s, w) => s + w.penRate, 0) / window.length;
                    return {
                        cumulativeMetres: pt.cumulativeMetres,
                        penRate: Math.round(avg * 10) / 10,
                    };
                });
                return { serial: h.serial, size: h.size, curve };
            });
    }, [filteredDrillingLogs]);

    // Bit manufacturer penetration rate comparison — grouped bar chart data.
    // Per diameter, average pen rate (m/hr) per manufacturer.
    const bitManufacturerData = useMemo(() => {
        const samples: Record<number, Record<string, number[]>> = {};
        for (const log of filteredDrillingLogs) {
            for (const c of log.consumables ?? []) {
                if (c.type !== 'drill-bit' || !c.manufacturer) continue;
                const linked = c.linkedHoleIds ?? [];
                const holes =
                    linked.length > 0
                        ? (log.drillHoles ?? []).filter((h) => linked.includes(h.id))
                        : (log.drillHoles ?? []);
                const totalM = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
                const totalS = holes.reduce((s, h) => s + (h.drillingTimeSeconds ?? 0), 0);
                if (totalS <= 0) continue;
                const dia = c.bitDiameter ?? 0;
                if (dia <= 0) continue;
                if (!samples[dia]) samples[dia] = {};
                if (!samples[dia][c.manufacturer]) samples[dia][c.manufacturer] = [];
                samples[dia][c.manufacturer].push((totalM / totalS) * 3600);
            }
        }
        return Object.entries(samples)
            .map(([dia, mfrs]) => ({
                diameter: Number(dia),
                manufacturers: Object.entries(mfrs)
                    .map(([manufacturer, vals]) => ({
                        manufacturer,
                        avgPenRate:
                            Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10,
                        count: vals.length,
                    }))
                    .sort((a, b) => b.avgPenRate - a.avgPenRate),
            }))
            .filter((g) => g.manufacturers.length > 0)
            .sort((a, b) => a.diameter - b.diameter);
    }, [filteredDrillingLogs]);

    const handleExportCSV = () => {
        const rows = filteredDrillingLogs.flatMap((log) =>
            (log.drillHoles ?? []).map((h) => [
                log.shiftDetails?.date ?? '',
                log.shiftDetails?.operatorName ?? '',
                log.shiftDetails?.rigId ?? '',
                log.shiftDetails?.site ?? '',
                log.shiftDetails?.shiftType ?? '',
                h.holeNumber ?? '',
                h.depth ?? 0,
                h.drillingTimeSeconds ? Math.round(h.drillingTimeSeconds / 60) : 0,
            ])
        );
        const headers = [
            'Date',
            'Operator',
            'Rig',
            'Site',
            'Shift',
            'Hole ID',
            'Depth (m)',
            'Drilling Time (min)',
        ];
        const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `drilling-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const applyPreset = (days: number | null) => {
        const now = new Date();
        setEndDate(now);
        setStartDate(days === null ? startOfMonth(now) : subDays(now, days));
    };

    const showDrilling = can('view:drilling') && hasApp('operator');

    const filterSummary = useMemo(() => {
        const parts: string[] = [];
        if (filteredDrillingLogs.length > 0)
            parts.push(`${filteredDrillingLogs.length} drilling shifts`);
        if (selectedSites.length > 0) {
            parts.push(
                selectedSites.length === 1 ? selectedSites[0] : `${selectedSites.length} sites`
            );
        }
        if (selectedAreas.length > 0) {
            parts.push(
                selectedAreas.length === 1 ? selectedAreas[0] : `${selectedAreas.length} areas`
            );
        }
        if (startDate && endDate) {
            parts.push(`${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`);
        }
        return parts.join(' · ');
    }, [filteredDrillingLogs, selectedSites, selectedAreas, startDate, endDate]);

    return (
        <div className='max-w-7xl mx-auto'>
            {/* Sticky filter bar */}
            <div className='sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border/50 -mx-4 px-4 pb-3 pt-2 mb-6'>
                <div className='flex flex-col gap-2'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <div className='flex flex-wrap items-center gap-2'>
                            {/* Multi-site filter */}
                            {availableSites.length > 0 && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant='outline' size='sm' className='gap-2'>
                                            <MapPin className='h-4 w-4' />
                                            {selectedSites.length === 0
                                                ? 'All Sites'
                                                : selectedSites.length === 1
                                                  ? selectedSites[0]
                                                  : `${selectedSites.length} sites`}
                                            <ChevronDown className='h-3 w-3 opacity-60' />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-64 p-2 z-50' align='start'>
                                        {selectedSites.length > 0 && (
                                            <button
                                                className='w-full text-left text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground mb-1'
                                                onClick={() => setSelectedSites([])}
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                        {availableSites.map((site) => {
                                            const checked = selectedSites.includes(site);
                                            return (
                                                <button
                                                    key={site}
                                                    className='flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent'
                                                    onClick={() =>
                                                        setSelectedSites(
                                                            checked
                                                                ? selectedSites.filter(
                                                                      (s) => s !== site
                                                                  )
                                                                : [...selectedSites, site]
                                                        )
                                                    }
                                                >
                                                    <Check
                                                        className={cn(
                                                            'h-4 w-4 shrink-0',
                                                            checked ? 'opacity-100' : 'opacity-0'
                                                        )}
                                                    />
                                                    {site}
                                                </button>
                                            );
                                        })}
                                    </PopoverContent>
                                </Popover>
                            )}

                            {/* Work area filter */}
                            {availableAreas.length > 0 && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant='outline' size='sm' className='gap-2'>
                                            <MapPin className='h-4 w-4' />
                                            {selectedAreas.length === 0
                                                ? 'All Areas'
                                                : selectedAreas.length === 1
                                                  ? selectedAreas[0]
                                                  : `${selectedAreas.length} areas`}
                                            <ChevronDown className='h-3 w-3 opacity-60' />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-72 p-2 z-50 max-h-64 overflow-y-auto' align='start'>
                                        {selectedAreas.length > 0 && (
                                            <button
                                                className='w-full text-left text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground mb-1'
                                                onClick={() => setSelectedAreas([])}
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                        {availableAreas.map((area) => {
                                            const checked = selectedAreas.includes(area);
                                            return (
                                                <button
                                                    key={area}
                                                    className='flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent'
                                                    onClick={() =>
                                                        setSelectedAreas(
                                                            checked
                                                                ? selectedAreas.filter(
                                                                      (a) => a !== area
                                                                  )
                                                                : [...selectedAreas, area]
                                                        )
                                                    }
                                                >
                                                    <Check
                                                        className={cn(
                                                            'h-4 w-4 shrink-0',
                                                            checked ? 'opacity-100' : 'opacity-0'
                                                        )}
                                                    />
                                                    {area}
                                                </button>
                                            );
                                        })}
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>

                        <div className='flex flex-wrap items-center gap-2'>
                            {/* Date presets */}
                            <div className='flex items-center gap-1 border border-border rounded-md px-1'>
                                {DATE_PRESETS.map((p) => (
                                    <button
                                        key={p.label}
                                        className='text-xs px-2 py-1 text-muted-foreground hover:text-foreground rounded transition-colors'
                                        onClick={() => applyPreset(p.days)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            {/* Date range pickers */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant='outline'
                                        size='sm'
                                        className={cn(
                                            'w-[150px] justify-start text-left font-normal',
                                            !startDate && 'text-muted-foreground'
                                        )}
                                    >
                                        <CalendarIcon className='mr-2 h-4 w-4' />
                                        {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className='w-auto p-0 z-50' align='start'>
                                    <Calendar
                                        mode='single'
                                        selected={startDate}
                                        onSelect={setStartDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                            <span className='text-xs text-muted-foreground'>to</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant='outline'
                                        size='sm'
                                        className={cn(
                                            'w-[150px] justify-start text-left font-normal',
                                            !endDate && 'text-muted-foreground'
                                        )}
                                    >
                                        <CalendarIcon className='mr-2 h-4 w-4' />
                                        {endDate ? format(endDate, 'MMM dd, yyyy') : 'End'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className='w-auto p-0 z-50' align='start'>
                                    <Calendar
                                        mode='single'
                                        selected={endDate}
                                        onSelect={setEndDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>

                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => void refetch()}
                                disabled={loading}
                            >
                                <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                            </Button>
                        </div>
                    </div>

                    {/* Filter summary */}
                    {filterSummary && (
                        <p className='text-xs text-muted-foreground leading-none'>
                            {filterSummary}
                        </p>
                    )}
                </div>
            </div>

            {loading ? (
                <Card className='bg-card border-border'>
                    <CardContent className='py-12 text-center'>
                        <RefreshCw className='h-8 w-8 text-primary mx-auto mb-4 animate-spin' />
                        <p className='text-muted-foreground'>Loading shift logs…</p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <RBACGuard permission='view:drilling' app='operator'>
                        {showDrilling && (
                            <div>
                                {/* Section header */}
                                {filteredDrillingLogs.length > 0 && (
                                    <div className='flex items-center justify-end gap-2 mb-4'>
                                        <PresentationButton
                                            sections={[
                                                {
                                                    id: 'metrics',
                                                    label: 'Metrics',
                                                    content: (
                                                        <DrillingMetricsCards
                                                            drillingMetrics={drillingMetrics}
                                                            delayMetrics={drillingDelayMetrics}
                                                            shiftCount={filteredDrillingLogs.length}
                                                            prevMetrics={prevMetrics}
                                                        />
                                                    ),
                                                },
                                                {
                                                    id: 'operators',
                                                    label: 'Operators',
                                                    content: (
                                                        <OperatorPerformance
                                                            operatorPerformance={operatorPerformanceData}
                                                            dayNightData={dayNightData}
                                                            leaderboards={operatorLeaderboards}
                                                        />
                                                    ),
                                                },
                                                {
                                                    id: 'charts',
                                                    label: 'Charts',
                                                    content: (
                                                        <DrillingCharts
                                                            dailyTrendData={dailyDrillingTrend}
                                                            penetrationTrendData={penetrationTrendData}
                                                            delayBreakdown={drillingDelayMetrics.delayBreakdown}
                                                            metresByRig={metresByRigData}
                                                            rigUtilisationData={rigUtilisationData}
                                                            hammerWearData={hammerWearData}
                                                            bitManufacturerData={bitManufacturerData}
                                                        />
                                                    ),
                                                },
                                                {
                                                    id: 'cpm',
                                                    label: 'Cost Per Metre',
                                                    content: <CpmAnalysis logs={filteredDrillingLogs} />,
                                                },
                                                {
                                                    id: 'consumables',
                                                    label: 'Consumables',
                                                    content: <ConsumableTables logs={filteredDrillingLogs} bitPerformance={metresPerBitData} hammerPerformance={metresPerHammerData} rodPerformance={metresPerRodData} />,
                                                },
                                                ...(drillPatterns.length > 0
                                                    ? [{
                                                        id: 'patterns',
                                                        label: 'Patterns',
                                                        content: (
                                                            <PatternProgress
                                                                patterns={drillPatterns}
                                                                drillingLogs={filteredDrillingLogs}
                                                            />
                                                        ),
                                                    }]
                                                    : []),
                                            ]}
                                        />
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={handleExportCSV}
                                            className='gap-2'
                                        >
                                            <Download className='h-4 w-4' />
                                            Export CSV
                                        </Button>
                                    </div>
                                )}

                                <DrillingMetricsCards
                                    drillingMetrics={drillingMetrics}
                                    delayMetrics={drillingDelayMetrics}
                                    shiftCount={filteredDrillingLogs.length}
                                    prevMetrics={prevMetrics}
                                />
                                <DrillingCharts
                                    dailyTrendData={dailyDrillingTrend}
                                    penetrationTrendData={penetrationTrendData}
                                    delayBreakdown={drillingDelayMetrics.delayBreakdown}
                                    metresByRig={metresByRigData}
                                    rigUtilisationData={rigUtilisationData}
                                    hammerWearData={hammerWearData}
                                    bitManufacturerData={bitManufacturerData}
                                />
                                <OperatorPerformance
                                    operatorPerformance={operatorPerformanceData}
                                    dayNightData={dayNightData}
                                    leaderboards={operatorLeaderboards}
                                />
                                <CpmAnalysis logs={filteredDrillingLogs} />
                                <ConsumableTables logs={filteredDrillingLogs} bitPerformance={metresPerBitData} hammerPerformance={metresPerHammerData} rodPerformance={metresPerRodData} />
                                {drillPatterns.length > 0 && (
                                    <PatternProgress
                                        patterns={drillPatterns}
                                        drillingLogs={filteredDrillingLogs}
                                    />
                                )}
                            </div>
                        )}
                    </RBACGuard>

                    {!showDrilling && (
                        <Card className='bg-card border-border'>
                            <CardContent className='py-12 text-center'>
                                <p className='text-muted-foreground'>
                                    No apps are enabled for your organisation.
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}
